/**
 * Fleet API Service
 * =================
 * API client for fleet management endpoints.
 * Uses axios with interceptors for auth and error handling.
 * 
 * Note: All fleet endpoints are under /api/fleet/*
 */

import axios from 'axios';

// Use same base URL pattern as main api.js
const API_BASE_URL = process.env.REACT_APP_API_URL || '';

// Normalize base URL to ensure it ends with /api
let normalizedBaseURL = API_BASE_URL || '';
if (normalizedBaseURL && !normalizedBaseURL.endsWith('/api')) {
  normalizedBaseURL = normalizedBaseURL.replace(/\/$/, '') + '/api';
} else if (!normalizedBaseURL) {
  normalizedBaseURL = '/api';
}

// Create axios instance
const api = axios.create({
  baseURL: normalizedBaseURL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Include cookies for httpOnly auth
});

// Request interceptor for auth token and CSRF
api.interceptors.request.use(
  (config) => {
    // Try to get token from localStorage (for Bearer auth)
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // Add CSRF token if available
    const csrfToken = localStorage.getItem('csrf_token');
    if (csrfToken && ['post', 'put', 'patch', 'delete'].includes(config.method?.toLowerCase())) {
      config.headers['X-CSRF-Token'] = csrfToken;
    }
    
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response.data,
  (error) => {
    if (error.response?.status === 401) {
      // Handle unauthorized - redirect to login
      localStorage.removeItem('access_token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    
    const message = error.response?.data?.detail || error.message || 'An error occurred';
    return Promise.reject(new Error(message));
  }
);

/**
 * Fleet API methods
 */
export const fleetApi = {
  // ==========================================================================
  // FLEETS
  // ==========================================================================
  
  /**
   * Create a new fleet
   */
  createFleet: async (data) => {
    return api.post('/fleets', data);
  },

  /**
   * List all fleets for current user
   */
  listFleets: async () => {
    return api.get('/fleets');
  },

  /**
   * Get fleet by ID
   */
  getFleet: async (fleetId) => {
    return api.get(`/fleets/${fleetId}`);
  },

  /**
   * Update fleet
   */
  updateFleet: async (fleetId, data) => {
    return api.put(`/fleets/${fleetId}`, data);
  },

  /**
   * Delete fleet
   */
  deleteFleet: async (fleetId) => {
    return api.delete(`/fleets/${fleetId}`);
  },

  /**
   * Get fleet dashboard stats
   */
  getDashboard: async (fleetId) => {
    return api.get(`/fleets/${fleetId}/dashboard`);
  },

  /**
   * Get upcoming maintenance alerts
   */
  getUpcomingMaintenance: async (fleetId, params = {}) => {
    return api.get(`/fleets/${fleetId}/maintenance/upcoming`, { params });
  },

  // ==========================================================================
  // VEHICLES
  // ==========================================================================

  /**
   * Add vehicle to fleet
   */
  addVehicle: async (fleetId, data) => {
    return api.post(`/fleets/${fleetId}/vehicles`, data);
  },

  /**
   * Bulk import vehicles
   */
  bulkImportVehicles: async (fleetId, data) => {
    return api.post(`/fleets/${fleetId}/vehicles/bulk`, data);
  },

  /**
   * List fleet vehicles with filters
   */
  listVehicles: async (fleetId, params = {}) => {
    return api.get(`/fleets/${fleetId}/vehicles`, { params });
  },

  /**
   * Get vehicle by ID
   */
  getVehicle: async (fleetId, vehicleId) => {
    return api.get(`/fleets/${fleetId}/vehicles/${vehicleId}`);
  },

  /**
   * Update vehicle
   */
  updateVehicle: async (fleetId, vehicleId, data) => {
    return api.put(`/fleets/${fleetId}/vehicles/${vehicleId}`, data);
  },

  /**
   * Remove vehicle from fleet
   */
  removeVehicle: async (fleetId, vehicleId) => {
    return api.delete(`/fleets/${fleetId}/vehicles/${vehicleId}`);
  },

  /**
   * Dispose vehicle
   */
  disposeVehicle: async (fleetId, vehicleId, data) => {
    return api.post(`/fleets/${fleetId}/vehicles/${vehicleId}/dispose`, data);
  },

  /**
   * Assign driver to vehicle
   */
  assignDriver: async (fleetId, vehicleId, driverId) => {
    return api.post(`/fleets/${fleetId}/vehicles/${vehicleId}/assign`, null, {
      params: { driver_id: driverId }
    });
  },

  // ==========================================================================
  // SERVICE RECORDS
  // ==========================================================================

  /**
   * Log service record for vehicle
   */
  logService: async (fleetId, vehicleId, data) => {
    return api.post(`/fleets/${fleetId}/vehicles/${vehicleId}/service`, data);
  },

  /**
   * Get vehicle service history
   */
  getServiceHistory: async (fleetId, vehicleId, params = {}) => {
    return api.get(`/fleets/${fleetId}/vehicles/${vehicleId}/history`, { params });
  },

  /**
   * List all service records for fleet
   */
  listServiceRecords: async (fleetId, params = {}) => {
    return api.get(`/fleets/${fleetId}/service-records`, { params });
  },

  /**
   * Update service record
   */
  updateServiceRecord: async (fleetId, recordId, data) => {
    return api.put(`/fleets/${fleetId}/service-records/${recordId}`, data);
  },

  /**
   * Delete service record
   */
  deleteServiceRecord: async (fleetId, recordId) => {
    return api.delete(`/fleets/${fleetId}/service-records/${recordId}`);
  },

  // ==========================================================================
  // COST REPORTS
  // ==========================================================================

  /**
   * Get cost report
   */
  getCostReport: async (fleetId, params) => {
    return api.get(`/fleets/${fleetId}/costs`, { params });
  },

  /**
   * Export cost report as CSV
   */
  exportCostReport: async (fleetId, params) => {
    const response = await api.get(`/fleets/${fleetId}/costs/export`, {
      params,
      responseType: 'blob',
    });
    return response;
  },

  // ==========================================================================
  // DEPARTMENTS
  // ==========================================================================

  /**
   * Create department
   */
  createDepartment: async (fleetId, data) => {
    return api.post(`/fleets/${fleetId}/departments`, data);
  },

  /**
   * List departments
   */
  listDepartments: async (fleetId) => {
    return api.get(`/fleets/${fleetId}/departments`);
  },

  /**
   * Get department by ID
   */
  getDepartment: async (fleetId, deptId) => {
    return api.get(`/fleets/${fleetId}/departments/${deptId}`);
  },

  /**
   * Update department
   */
  updateDepartment: async (fleetId, deptId, data) => {
    return api.put(`/fleets/${fleetId}/departments/${deptId}`, data);
  },

  /**
   * Delete department
   */
  deleteDepartment: async (fleetId, deptId) => {
    return api.delete(`/fleets/${fleetId}/departments/${deptId}`);
  },

  // ==========================================================================
  // DRIVERS
  // ==========================================================================

  /**
   * Create driver
   */
  createDriver: async (fleetId, data) => {
    return api.post(`/fleets/${fleetId}/drivers`, data);
  },

  /**
   * List drivers
   */
  listDrivers: async (fleetId, params = {}) => {
    return api.get(`/fleets/${fleetId}/drivers`, { params });
  },

  /**
   * Get driver by ID
   */
  getDriver: async (fleetId, driverId) => {
    return api.get(`/fleets/${fleetId}/drivers/${driverId}`);
  },

  /**
   * Update driver
   */
  updateDriver: async (fleetId, driverId, data) => {
    return api.put(`/fleets/${fleetId}/drivers/${driverId}`, data);
  },

  /**
   * Delete driver
   */
  deleteDriver: async (fleetId, driverId) => {
    return api.delete(`/fleets/${fleetId}/drivers/${driverId}`);
  },

  // ==========================================================================
  // VIN DECODE
  // ==========================================================================

  /**
   * Decode VIN
   */
  decodeVin: async (vin) => {
    return api.get(`/vin/decode/${vin}`);
  },

  // ==========================================================================
  // AI COST ESTIMATE
  // ==========================================================================

  /**
   * Get AI cost estimate for service
   */
  getCostEstimate: async (vehicleId, serviceType, description) => {
    return api.post(`/vehicles/${vehicleId}/cost-estimate`, {
      service_type: serviceType,
      description,
    });
  },
};

export default fleetApi;
